// TranslatorScreen — submit + monitor video translation jobs.
//
// Backed by transcriber-api's `translation_jobs` routes
// (nayto-translator/apps/api/README.md, v0.9.0):
//   POST   /api/translations              — submit (201)
//   GET    /api/translations[?all=true]   — list (own / everyone for admin+manager)
//                                           SLIM rows: no segments / summaries /
//                                           sourceUrl
//   GET    /api/translations/:id          — full document
//   POST   /api/translations/:id/retry    — retry (200)
//   DELETE /api/translations/:id          — delete (204, no body)
//
// The screen is login-gated but role-open: any non-pending user can
// submit a job and see their own. Admin/manager get a "Show all
// users" toggle, an owner column, and the cost-estimate field on the
// detail panel.
//
// While any job is active (`pending|claimed|processing`) we poll the
// list every 15s; we stop when every visible job is terminal so the
// idle state isn't burning requests. Expanding a row fetches its full
// document into a per-id cache; while that job is active the document
// is refreshed on the same 15s tick as the list.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { flushSync } from 'react-dom';
import { FONT_STACK, type AccentTokens, type ThemeTokens } from '@nayto/theme';
import { isRTL, tFn, type Lang } from '@nayto/i18n';
import {
  FIREBASE_SIGNED_URL_RE,
  TRANSLATION_ACTIVE_STATUSES,
  TRANSLATION_POLL_INTERVAL_MS,
  TRANSLATION_RETRY_LIMIT,
  type BrowserTranslationSourceType,
  type TranslationConflictBody,
  type TranslationErrorCategory,
  type TranslationFailedChunk,
  type TranslationJob,
  type TranslationSegment,
  type TranslationSpeaker,
  type TranslationsListResponse,
  type TranslationStatus,
} from '@nayto/types';
import { apiFetch, getFirebaseAuth, getStorageConfig, useMe } from '@/auth';
import { Chip } from '@/components/data-display/Chip';
import { EmptyState } from '@/components/feedback/EmptyState';
import { ErrorPanel } from '@/components/feedback/ErrorPanel';
import { Skeleton, SkeletonStyles } from '@/components/feedback/Skeleton';
import { Icon } from '@/components/icons';
import {
  speakerLabel,
  speakerMode,
  speakerPrefix,
  speakerRunStarts,
} from '@/utils/speakers';
import { sourceTypeLabel } from '@/utils/source-type-label';
import {
  MEDIA_FILE_ACCEPT,
  SUPPORTED_FORMATS_LABEL,
  createEtaTracker,
  estimateCostUsd,
  estimateProcessingSeconds,
  formatBytes,
  formatEta,
  isSupportedMediaFile,
  uploadSizeVerdict,
} from '@/utils/media-file';
import { probeMediaDuration } from '@/utils/media-duration';
import { sourceUrlErrorKey, validateSourceUrl } from '@/utils/source-url';
import { canRetryJob, isFinalFailure } from '@/utils/retry-policy';
import {
  formatChunkDetails,
  formatChunkRange,
  mergeSegmentsWithGaps,
  missingChunks,
} from '@/utils/missing-chunks';
import {
  ResumableUploader,
  clearResumableSession,
  fingerprintMatches,
  readResumableSession,
  type ResumableSessionRecord,
} from '@/utils/resumable-upload';
import { formatStorageError, isStorageCanceled } from '@/utils/storage-errors';
import { useWakeLock } from '@/hooks/useWakeLock';
import { useMediaQuery } from '@/hooks/useMediaQuery';

// ─── Types + helpers ─────────────────────────────────────────────────

const RESULT_TABS: Array<{ k: Lang; l: string }> = [
  { k: 'en', l: 'EN' },
  { k: 'he', l: 'HE' },
  { k: 'hy', l: 'HY' },
  { k: 'ru', l: 'RU' },
];

function isJsonObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v != null && !Array.isArray(v);
}

/** Parse a non-OK response into a structured `{error, ...}` body when
 * the backend returned JSON, otherwise fall through to a generic
 * Error. Used by submit / retry / delete handlers to differentiate
 * 409 sub-codes (`invalid_status` vs `retry_exhausted`). */
async function readErrorBody(res: Response): Promise<TranslationConflictBody | null> {
  try {
    const j: unknown = await res.clone().json();
    if (isJsonObject(j) && typeof j['error'] === 'string') {
      return j as unknown as TranslationConflictBody;
    }
  } catch {
    /* not JSON — caller falls back to status code message */
  }
  return null;
}

function fmtMmSs(totalSec: number): string {
  if (!Number.isFinite(totalSec) || totalSec < 0) return '00:00';
  const m = Math.floor(totalSec / 60);
  const s = Math.floor(totalSec % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function fmtDuration(seconds: number | null | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m === 0) return `${s}s`;
  return `${m}m${String(s).padStart(2, '0')}s`;
}

function fmtUsd(usd: number | null | undefined): string | null {
  if (usd == null || !Number.isFinite(usd)) return null;
  return `$${usd.toFixed(usd < 1 ? 4 : 2)}`;
}

function fmtTimestamp(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('en-GB', {
      timeZone: 'Asia/Yerevan',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return null;
  }
}

/** What the browser submits: only `url` or `firebase` — never `upload`,
 * which is a server-side (multipart) job type. */
function detectSourceType(url: string): BrowserTranslationSourceType {
  return FIREBASE_SIGNED_URL_RE.test(url) ? 'firebase' : 'url';
}

function jobLabel(job: TranslationJob, t: (key: string) => string): string {
  return (
    job.title ?? job.englishTitle ?? job.originalFilename ?? sourceTypeLabel(job.sourceType, t)
  );
}

function ownerLabel(job: TranslationJob): string | null {
  return (
    job.owner?.displayName ?? job.owner?.email ?? job.owner?.phoneNumber ?? job.ownerUid ?? null
  );
}

function statusColor(status: TranslationStatus, accent: AccentTokens): string {
  if (status === 'complete') return '#16a34a';
  if (status === 'failed') return '#dc2626';
  if (status === 'pending') return '#6b7280';
  return accent.main;
}

/** Best-effort clipboard write. `navigator.clipboard.writeText` is the
 * happy path; on http (some local dev) or older browsers it fails, so
 * we fall back to a temporary `<textarea>` + `document.execCommand`.
 * Returns `true` only when one of the strategies succeeded. */
async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/** Compose the active-lang result into a paste-ready text block.
 * Format (skips empty parts):
 *   <Title>
 *
 *   Participants: A, B, C
 *
 *   <Summary>
 *
 *   [mm:ss]  segment line
 *   [mm:ss]  segment line
 */
function formatResultForCopy(job: TranslationJob, lang: Lang): string {
  const parts: string[] = [];
  const title =
    lang === 'en'
      ? (job.englishTitle ?? job.title ?? null)
      : (job.title ?? job.englishTitle ?? null);
  if (title) parts.push(title);
  if (job.participants && job.participants.length > 0) {
    parts.push(`Participants: ${job.participants.join(', ')}`);
  }
  const summary = job.summaries?.[lang];
  if (summary) parts.push(summary);
  // F-09: the copied transcript carries the speaker labels the screen
  // shows. Same module, so the two cannot drift — and unlike the screen
  // this repeats the label on every line, because copied text gets cut,
  // pasted and quoted a line at a time.
  const showSpeakers = speakerMode(job) !== 'off';
  const t = tFn(lang);
  const lines = (job.segments ?? [])
    .map((seg) => {
      const text = seg[lang];
      if (typeof text !== 'string' || !text) return null;
      const who = showSpeakers ? speakerPrefix(seg.speaker, job.speakers, t) : '';
      return `[${fmtMmSs(seg.start)}]  ${who}${text}`;
    })
    .filter((l): l is string => l != null);
  if (lines.length > 0) parts.push(lines.join('\n'));
  return parts.join('\n\n');
}

/** Deep-link to a specific job. The receiver's TranslatorScreen reads
 * `?job=` on mount, pre-expands the matching row, and strips the param
 * from the URL. Visibility still gated by the API: a non-owner who
 * isn't admin/manager will see "Job not found" since the list endpoint
 * won't return that doc. */
function buildShareUrl(jobId: string): string {
  try {
    const u = new URL(window.location.href);
    u.searchParams.set('route', 'translator');
    u.searchParams.set('job', jobId);
    u.hash = '';
    return u.toString();
  } catch {
    return `${window.location.origin}/?route=translator&job=${encodeURIComponent(jobId)}`;
  }
}

// F-02: was `if (rtl) { …Hebrew… } return status`, i.e. every non-Hebrew
// reader saw the raw API enum. All five states now come from the
// dictionary in all four languages.
function statusLabel(status: TranslationStatus, t: (key: string) => string): string {
  if (status === 'pending') return t('trStatusPending');
  if (status === 'claimed') return t('trStatusClaimed');
  if (status === 'processing') return t('trStatusProcessing');
  if (status === 'complete') return t('trStatusComplete');
  return t('trStatusFailed');
}

/** Lightweight in-memory toast queue — three-tier severity, auto-dismiss
 * 4s. Avoids pulling in a toast library; the UX is single-line so a
 * full notification stack isn't needed yet. */
interface Toast {
  id: number;
  kind: 'info' | 'error' | 'success';
  message: string;
}

function useToasts(): {
  toasts: Toast[];
  push: (kind: Toast['kind'], message: string) => void;
} {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);
  const push = useCallback((kind: Toast['kind'], message: string) => {
    idRef.current += 1;
    const id = idRef.current;
    setToasts((prev) => [...prev, { id, kind, message }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((tt) => tt.id !== id));
    }, 4000);
  }, []);
  return { toasts, push };
}

// ─── Screen ──────────────────────────────────────────────────────────

interface TranslatorScreenProps {
  theme: ThemeTokens;
  accent: AccentTokens;
  lang: Lang;
}

export function TranslatorScreen({ theme, accent, lang }: TranslatorScreenProps) {
  const rtl = isRTL(lang);
  const t = tFn(lang);
  const { me } = useMe();
  const role = me?.role ?? 'user';
  const isAdminOrManager = role === 'admin' || role === 'manager';

  // Default the cross-user view ON for admin/manager — they almost
  // always want to see the whole team's queue first, not just their
  // own submissions. The toggle is still there for the rare case
  // they want to filter to their own jobs.
  const [showAll, setShowAll] = useState(true);
  // Reset the "Show all" toggle once we know the user lacks privilege.
  // Skip while `me` is still loading — otherwise the default-true would
  // get clobbered to false on initial mount (role defaults to 'user'
  // before /api/me resolves) and an admin would land with the toggle
  // off despite the new default.
  useEffect(() => {
    if (!me) return;
    if (!isAdminOrManager && showAll) setShowAll(false);
  }, [me, isAdminOrManager, showAll]);

  const [jobs, setJobs] = useState<TranslationJob[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const triggerReload = useCallback(() => setReloadNonce((n) => n + 1), []);

  // Deep-link: `?route=translator&job=<id>` arrives via shared links.
  // Read once on mount and pre-seed the expanded row; the param is then
  // stripped from the URL so a refresh doesn't keep re-expanding it.
  const initialDeepJobId = useMemo<string | null>(() => {
    try {
      const id = new URLSearchParams(window.location.search).get('job');
      return id || null;
    } catch {
      return null;
    }
  }, []);
  const [expandedId, setExpandedId] = useState<string | null>(initialDeepJobId);
  useEffect(() => {
    if (!initialDeepJobId) return;
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('job');
      window.history.replaceState(null, '', u.toString());
    } catch {
      /* ignore — browser may block on data: URLs etc. */
    }
  }, [initialDeepJobId]);

  const { toasts, push: pushToast } = useToasts();

  // ─── Fetch list ────────────────────────────────────────────────────
  // Keyed on the effective scope, not on `showAll` itself: for a plain
  // user the toggle is reset to false right after mount, and re-running
  // the fetch on that flip would issue the same own-scope request twice.
  const listAll = showAll && isAdminOrManager;
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    const qs = listAll ? '?all=true' : '';
    apiFetch(`/api/translations${qs}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<TranslationsListResponse>;
      })
      .then((j) => {
        if (cancelled) return;
        setJobs(j.jobs ?? []);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadNonce, listAll]);

  // ─── Polling ───────────────────────────────────────────────────────
  // Poll the list while any job is in an active state. Re-evaluates
  // after each successful fetch so we stop the moment the last job
  // hits a terminal state.
  const hasActiveJob = useMemo(() => {
    if (!jobs) return false;
    return jobs.some((j) => TRANSLATION_ACTIVE_STATUSES.has(j.status));
  }, [jobs]);

  useEffect(() => {
    if (!hasActiveJob) return undefined;
    const id = window.setInterval(triggerReload, TRANSLATION_POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [hasActiveJob, triggerReload]);

  // Refetch on tab focus — same UX rationale as StatsScreen.
  useEffect(() => {
    const onFocus = () => triggerReload();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [triggerReload]);

  // ─── Job detail cache ──────────────────────────────────────────────
  // The list endpoint returns slim rows (no segments / summaries /
  // sourceUrl). The full document is fetched when a row is expanded and
  // kept per id. It is refetched (a) on every poll tick while the job
  // is active — same `reloadNonce` the list uses — and (b) once more
  // when the list row shows an `updatedAt` newer than the cached
  // document (the job finished between two detail fetches).
  const [details, setDetails] = useState<Record<string, TranslationJob>>({});
  const [detailErrors, setDetailErrors] = useState<Record<string, string>>({});
  const [detailNonce, setDetailNonce] = useState(0);
  const retryDetail = useCallback(() => setDetailNonce((n) => n + 1), []);

  const expandedRow = useMemo(
    () => (expandedId ? (jobs?.find((j) => j._id === expandedId) ?? null) : null),
    [jobs, expandedId],
  );
  const expandedRowStatus = expandedRow?.status ?? null;
  const expandedRowUpdatedAt = expandedRow?.updatedAt ?? null;

  useEffect(() => {
    if (!expandedId) return undefined;
    const id = expandedId;
    const cached = details[id];
    const isActive =
      expandedRowStatus != null && TRANSLATION_ACTIVE_STATUSES.has(expandedRowStatus);
    const stale =
      cached != null && expandedRowUpdatedAt != null && cached.updatedAt !== expandedRowUpdatedAt;
    if (cached && !isActive && !stale) return undefined;

    let cancelled = false;
    apiFetch(`/api/translations/${encodeURIComponent(id)}`)
      .then(async (r) => {
        if (r.status === 404) {
          throw new Error(t('trJobNotFound'));
        }
        if (!r.ok) {
          const body = await readErrorBody(r).catch(() => null);
          throw new Error(body?.error ? `${body.error} (HTTP ${r.status})` : `HTTP ${r.status}`);
        }
        return r.json() as Promise<TranslationJob>;
      })
      .then((doc) => {
        if (cancelled) return;
        setDetails((prev) => ({ ...prev, [id]: doc }));
        setDetailErrors((prev) => {
          if (!(id in prev)) return prev;
          const next = { ...prev };
          delete next[id];
          return next;
        });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setDetailErrors((prev) => ({
          ...prev,
          [id]: e instanceof Error ? e.message : String(e),
        }));
      });
    return () => {
      cancelled = true;
    };
    // `details` is read here but written by this same effect — listing
    // it would re-run the effect on its own write. The row's status /
    // updatedAt + the two nonces are the real triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedId, expandedRowStatus, expandedRowUpdatedAt, reloadNonce, detailNonce, t]);

  // ─── Submit ────────────────────────────────────────────────────────
  const [submitUrl, setSubmitUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // ─── Upload state machine (R-11 / R-13, phone uploads) ───────────
  //   awaiting-file → a session from an earlier page load exists in
  //                   sessionStorage; ask the user to re-pick the same
  //                   file so we can continue from the server's offset
  //   confirm       → file > 300 MB: ask before starting, explain the
  //                   link alternative, show duration / estimates
  //   uploading     → our ResumableUploader is pushing chunks (8 MiB)
  //   paused        → we paused (browser offline); resumes on `online`
  //   interrupted   → the uploader is parked after a network failure, or
  //                   it failed for good (rules, quota). "Tap to continue"
  //                   resumes from the server's offset — never from 0.
  // The File stays in state so a resume never needs the picker again.
  const [upload, setUpload] = useState<UploadUiState | null>(null);
  const uploaderRef = useRef<ResumableUploader | null>(null);
  const etaRef = useRef(createEtaTracker());
  const isUploadingNow =
    upload?.phase === 'uploading' || upload?.phase === 'paused' || upload?.phase === 'finalizing';
  const isDesktop = useMediaQuery('(min-width: 768px)');

  // Keep the screen on while bytes are moving (silent no-op when the
  // browser lacks the API).
  const wakeLock = useWakeLock(upload?.phase === 'uploading' || upload?.phase === 'finalizing');

  // Warn before leaving the page mid-upload.
  useEffect(() => {
    if (!isUploadingNow) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isUploadingNow]);

  // A refresh mid-upload loses the File object but not the Storage
  // session: its URL + a fingerprint of the file are in sessionStorage.
  // Offer to continue from where the server is once the same file is
  // picked again.
  useEffect(() => {
    let rec: ResumableSessionRecord | null = null;
    try {
      rec = readResumableSession(window.sessionStorage);
    } catch {
      /* ignore */
    }
    if (rec && rec.fingerprint.size > 0) {
      setUpload({
        phase: 'awaiting-file',
        file: null,
        record: rec,
        progress: (rec.lastOffset ?? 0) / rec.fingerprint.size,
        bytesTransferred: rec.lastOffset ?? 0,
        etaSeconds: null,
      });
    }
  }, []);

  // Offline → pause (so no chunk is burnt into a dead socket); online or
  // return to the foreground → resume. iOS suspends the page on screen
  // lock: the in-flight chunk dies, the uploader parks in 'interrupted',
  // and the visibilitychange on unlock nudges it to query + continue.
  useEffect(() => {
    if (!isUploadingNow && upload?.phase !== 'interrupted') return undefined;
    const onOffline = () => uploaderRef.current?.pause();
    const onOnline = () => uploaderRef.current?.resume();
    const onVisible = () => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        uploaderRef.current?.resume();
      }
    };
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isUploadingNow, upload?.phase]);

  /** Translate API errors into the user-facing toast + persistent
   * inline-error UX once the POST is in-flight. Same pattern for both
   * URL and Firebase paths.
   *
   * Why both toast + inline: the toast catches the user's attention
   * mid-action, then fades. The inline error sticks until the next
   * submit so the user can read the real `body.error` after the toast
   * is gone — critical for diagnosing the "submit silently failed"
   * class of bug we hit when ownerEmail validation rejected a real
   * upload and the user thought the system was broken.
   *
   * `orphanPath`, if provided, is appended to the inline error so a
   * file that uploaded successfully but couldn't be submitted is
   * surfaced for manual recovery instead of being lost. */
  const handleSubmitResponse = useCallback(
    async (res: Response, opts: { orphanPath?: string | null } = {}): Promise<boolean> => {
      if (res.ok) return true;
      const body = await readErrorBody(res).catch(() => null);
      const detail = body?.error ?? `HTTP ${res.status}`;
      const orphanLine = opts.orphanPath
        ? t('trOrphanFileUploaded').replace('{path}', opts.orphanPath)
        : '';

      if (res.status === 429) {
        // 429 is self-explanatory — keep the toast-only UX. No persistent
        // inline error needed: the user knows exactly what to do.
        pushToast(
          'error',
          t('trTooManyActiveJobs'),
        );
        return false;
      }

      if (res.status >= 500) {
        setSubmitError(
          t('trServerErrorWithStatus')
            .replace('{status}', String(res.status))
            .replace('{detail}', detail) + orphanLine,
        );
        pushToast(
          'error',
          t('trServerError'),
        );
        return false;
      }

      if (res.status === 400) {
        // `invalid_source_url` is the one 400 a user can act on: say so in
        // words (the local check normally catches it first — this is the
        // fallback for URLs that parse but the API still rejects).
        const readable =
          body?.error === 'invalid_source_url' ? `${t('errInvalidSourceUrl')} (${detail})` : null;
        setSubmitError(
          (readable ?? t('trInvalidRequest').replace('{detail}', detail)) +
            orphanLine,
        );
        return false;
      }

      // Other non-ok (401/403/404/409/…) — still capture body.error
      // inline so the user can read what came back.
      setSubmitError(
        t('trErrorWithStatus').replace('{status}', String(res.status)).replace('{detail}', detail) +
          orphanLine,
      );
      pushToast('error', `HTTP ${res.status} — ${detail}`);
      return false;
    },
    [t, pushToast],
  );

  // Local URL verdict: the button stays disabled and the field shows why
  // until the text parses as an http(s) URL — no request leaves for
  // plain text (Q-02 finding).
  const urlVerdict = useMemo(() => validateSourceUrl(submitUrl), [submitUrl]);
  const urlFieldError =
    submitUrl.trim() !== '' && !urlVerdict.ok ? t(sourceUrlErrorKey(urlVerdict.reason)) : null;

  const onSubmitUrl = useCallback(async () => {
    const verdict = validateSourceUrl(submitUrl);
    if (!verdict.ok) {
      setSubmitError(t(sourceUrlErrorKey(verdict.reason)));
      return;
    }
    const url = verdict.url;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const res = await apiFetch('/api/translations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceType: detectSourceType(url),
          sourceUrl: url,
        }),
      });
      const ok = await handleSubmitResponse(res);
      if (!ok) return;
      setSubmitUrl('');
      pushToast('success', t('trJobSubmitted'));
      triggerReload();
    } catch (e) {
      pushToast('error', e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }, [submitUrl, t, pushToast, triggerReload, handleSubmitResponse]);

  /** Read the media duration in the background and attach it to the
   * current upload state (drives the duration / processing / cost
   * estimates). Never blocks, never throws. */
  const attachDuration = useCallback((file: File) => {
    void probeMediaDuration(file).then((d) => {
      setUpload((u) => (u && u.file === file ? { ...u, durationSec: d } : u));
    });
  }, []);

  /** Run our resumable upload of `file` (new session, or `resumeFrom` an
   * earlier one), then submit the job. Network failures do not end it:
   * the uploader parks and continues on online / foreground / tap. */
  const startUpload = useCallback(
    async (file: File, resumeFrom: ResumableSessionRecord | null = null) => {
      const user = getFirebaseAuth().currentUser;
      if (!user) {
        setSubmitError(t('trSignInRequired'));
        setUpload(null);
        return;
      }
      let storageCfg: { bucket: string; baseUrl: string };
      try {
        storageCfg = getStorageConfig();
      } catch (e) {
        setSubmitError(e instanceof Error ? e.message : String(e));
        setUpload(null);
        return;
      }
      setSubmitError(null);
      setSubmitting(true);
      etaRef.current.reset();
      const startOffset = resumeFrom?.lastOffset ?? 0;
      setUpload((u) => ({
        phase: 'uploading',
        file,
        record: resumeFrom,
        progress: file.size > 0 ? startOffset / file.size : 0,
        bytesTransferred: startOffset,
        etaSeconds: null,
        resumedFromPct: resumeFrom
          ? Math.round((startOffset / Math.max(file.size, 1)) * 100)
          : null,
        durationSec: u && u.file === file ? u.durationSec : undefined,
      }));
      const safeName = file.name.replace(/[^\w.-]+/g, '_');
      const path = resumeFrom?.path ?? `translations/uploads/${user.uid}/${Date.now()}_${safeName}`;
      const uploader = new ResumableUploader({
        file,
        bucket: storageCfg.bucket,
        baseUrl: storageCfg.baseUrl,
        path,
        contentType: file.type || 'application/octet-stream',
        getToken: async () => {
          const u = getFirebaseAuth().currentUser;
          return u ? await u.getIdToken(false) : null;
        },
        storage: window.sessionStorage,
        resumeFrom,
        onProgress: (bytes, total) => {
          const eta = etaRef.current.update(bytes, total);
          setUpload((u) =>
            u && u.phase !== 'awaiting-file' && u.phase !== 'confirm'
              ? {
                  ...u,
                  progress: total > 0 ? bytes / total : 0,
                  bytesTransferred: bytes,
                  etaSeconds: eta,
                }
              : u,
          );
        },
        onStateChange: (state) => {
          setUpload((u) => {
            if (!u || u.phase === 'awaiting-file' || u.phase === 'confirm') return u;
            if (state === 'uploading' || state === 'starting') {
              return { ...u, phase: 'uploading', errorMessage: undefined };
            }
            if (state === 'paused') return { ...u, phase: 'paused' };
            if (state === 'done') {
              // Bytes are all on the server; the job is not created yet.
              return {
                ...u,
                phase: 'finalizing',
                progress: 1,
                bytesTransferred: u.file.size,
                etaSeconds: null,
              };
            }
            if (state === 'interrupted') {
              // Parked after a network failure — the uploader itself
              // resumes on online/foreground; the button does the same.
              return { ...u, phase: 'interrupted', errorMessage: undefined };
            }
            return u;
          });
        },
      });
      uploaderRef.current = uploader;
      try {
        const result = await uploader.run();
        uploaderRef.current = null;
        // Q-02: after 100 % the panel went blank until the job appeared.
        // "Finishing upload…" covers the finalize + POST window.
        setUpload((u) =>
          u && u.phase !== 'awaiting-file'
            ? {
                ...u,
                phase: 'finalizing',
                progress: 1,
                bytesTransferred: file.size,
                etaSeconds: null,
              }
            : u,
        );
        const res = await apiFetch('/api/translations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sourceType: 'firebase',
            sourceUrl: result.downloadUrl,
            originalFilename: file.name,
          }),
        });
        setUpload(null);
        // Pass the storage path so a failed submit after a successful
        // upload surfaces the orphan file's location in the inline
        // error instead of leaving the user with an invisible blob.
        const ok = await handleSubmitResponse(res, { orphanPath: result.path });
        if (!ok) return;
        pushToast('success', t('sentToTranslation'));
        triggerReload();
      } catch (e) {
        uploaderRef.current = null;
        if (isStorageCanceled(e)) {
          // User clicked Cancel — quiet.
          setUpload(null);
          pushToast('info', t('storageCanceled'));
        } else {
          // Permanent failure (rules, quota, …): keep the File so "tap to
          // continue" can start a fresh session.
          const message = formatStorageError(e, t);
          setUpload((u) =>
            u && u.phase !== 'awaiting-file'
              ? { ...u, phase: 'interrupted', record: null, errorMessage: message }
              : u,
          );
          pushToast('error', t('uploadInterrupted'));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [t, pushToast, triggerReload, handleSubmitResponse],
  );

  /** Picker entry point: type gate, size ceiling, resume-after-refresh
   * match, large-file confirmation. No Storage traffic happens here. */
  const onSubmitFile = useCallback(
    (file: File) => {
      // Gate BEFORE any Storage traffic: the `accept` attribute is only a
      // hint, and QA saw a .txt get uploaded. Extension decides (see
      // utils/media-file.ts).
      if (!isSupportedMediaFile(file)) {
        const msg = `${t('unsupportedFileType')}: ${file.name}\n${t('supportedFormats')}: ${SUPPORTED_FORMATS_LABEL}`;
        setSubmitError(msg);
        pushToast('error', t('unsupportedFileType'));
        return;
      }
      const verdict = uploadSizeVerdict(file.size);
      if (verdict === 'empty') {
        setSubmitError(`${t('fileEmpty')} ${file.name}`);
        pushToast('error', t('fileEmpty'));
        return;
      }
      if (verdict === 'too_large') {
        setSubmitError(`${t('fileTooLarge')} (${formatBytes(file.size)})`);
        pushToast('error', t('fileTooLarge'));
        return;
      }
      // Continuing an earlier session? Only when it is the very same file
      // (name + size + lastModified); anything else starts fresh.
      const pending = upload?.phase === 'awaiting-file' ? upload.record : null;
      if (pending) {
        if (fingerprintMatches(pending, file)) {
          attachDuration(file);
          void startUpload(file, pending);
          return;
        }
        clearResumableSession(window.sessionStorage);
      }
      attachDuration(file);
      if (verdict === 'warn') {
        setSubmitError(null);
        setUpload({
          phase: 'confirm',
          file,
          record: null,
          progress: 0,
          bytesTransferred: 0,
          etaSeconds: null,
          durationSec: undefined,
        });
        return;
      }
      void startUpload(file);
    },
    [t, pushToast, startUpload, attachDuration, upload],
  );

  const onConfirmUpload = useCallback(() => {
    if (upload?.phase === 'confirm') void startUpload(upload.file);
  }, [upload, startUpload]);

  const onResumeUpload = useCallback(() => {
    const uploader = uploaderRef.current;
    if (uploader) {
      // Parked (network) or paused: continue from the server's offset.
      uploader.resume();
      return;
    }
    // The uploader is gone (permanent failure) — start a fresh session.
    if (upload && upload.phase === 'interrupted') void startUpload(upload.file);
  }, [upload, startUpload]);

  const onCancelUpload = useCallback(() => {
    const uploader = uploaderRef.current;
    if (uploader) {
      // run() rejects with storage/canceled and the catch above clears
      // the state and the session record.
      uploader.cancel();
      return;
    }
    // confirm / interrupted-without-uploader / awaiting-file: nothing in
    // flight, just drop the File and forget any stored session.
    clearResumableSession(window.sessionStorage);
    setUpload(null);
  }, []);

  // ─── Per-job actions ──────────────────────────────────────────────
  const onRetry = useCallback(
    async (job: TranslationJob) => {
      try {
        const res = await apiFetch(`/api/translations/${job._id}/retry`, {
          method: 'POST',
        });
        if (res.status === 404) {
          pushToast('error', t('trJobNotFound'));
          triggerReload();
          return;
        }
        if (res.status === 409) {
          const body = await readErrorBody(res);
          if (body?.error === 'retry_exhausted') {
            pushToast(
              'error',
              t('trMaxRetriesReached'),
            );
          } else {
            pushToast('error', t('trCannotRetryRunning'));
          }
          return;
        }
        if (!res.ok) {
          pushToast('error', `HTTP ${res.status}`);
          return;
        }
        pushToast('info', t('trJobRequeued'));
        triggerReload();
      } catch (e) {
        pushToast('error', e instanceof Error ? e.message : String(e));
      }
    },
    [t, pushToast, triggerReload],
  );

  const onDelete = useCallback(
    async (job: TranslationJob) => {
      const confirmMsg = t('trDeleteConfirm');
      if (!window.confirm(confirmMsg)) return;
      try {
        const res = await apiFetch(`/api/translations/${job._id}`, {
          method: 'DELETE',
        });
        if (res.status === 404) {
          pushToast('error', t('trJobNotFound'));
          triggerReload();
          return;
        }
        if (res.status === 409) {
          pushToast('error', t('trCannotDeleteRunning'));
          return;
        }
        if (!res.ok) {
          pushToast('error', `HTTP ${res.status}`);
          return;
        }
        // 204 — no body to read. Drop the cached document too.
        pushToast('info', t('trJobDeleted'));
        if (expandedId === job._id) setExpandedId(null);
        setDetails((prev) => {
          if (!(job._id in prev)) return prev;
          const next = { ...prev };
          delete next[job._id];
          return next;
        });
        triggerReload();
      } catch (e) {
        pushToast('error', e instanceof Error ? e.message : String(e));
      }
    },
    [t, pushToast, triggerReload, expandedId],
  );

  const sortedJobs = useMemo(() => {
    if (!jobs) return null;
    return [...jobs].sort((a, b) => {
      const ta = new Date(a.createdAt).getTime();
      const tb = new Date(b.createdAt).getTime();
      return tb - ta;
    });
  }, [jobs]);

  // ─── Render ────────────────────────────────────────────────────────

  return (
    <div dir={rtl ? 'rtl' : 'ltr'} style={{ padding: 16, maxWidth: 1100, margin: '0 auto' }}>
      {/* Print stylesheet — drives the "PDF" button on each result.
       * The trick: while printing, hide everything in the body except
       * elements marked `.nayto-print-target` and their descendants.
       * `visibility: hidden` preserves layout (so the cascading
       * position of the expanded card doesn't matter), and the target
       * gets positioned at the top-left of the page so the printable
       * area starts cleanly. Segment list's scroll cap is also lifted
       * so the full transcript appears across pages instead of being
       * cropped at the on-screen 360px height. */}
      <style>{`
        @media print {
          @page { margin: 14mm; }
          body * { visibility: hidden !important; }
          .nayto-print-target,
          .nayto-print-target * { visibility: visible !important; }
          .nayto-print-target {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            background: #fff !important;
            color: #000 !important;
            padding: 0 !important;
          }
          .nayto-print-target [data-print-scroll] {
            max-height: none !important;
            overflow: visible !important;
          }
        }
      `}</style>
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: theme.text,
            letterSpacing: -0.3,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Icon name="translate" size={18} />
          {t('trTitle')}
        </div>
        <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 4 }}>{t('uploadTitle')}</div>
      </div>

      <SubmitForm
        url={submitUrl}
        urlValid={urlVerdict.ok}
        urlError={urlFieldError}
        formatsLabel={t('supportedFormats')}
        onUrlChange={setSubmitUrl}
        onSubmitUrl={onSubmitUrl}
        onSubmitFile={onSubmitFile}
        onCancelUpload={onCancelUpload}
        onConfirmUpload={onConfirmUpload}
        onResumeUpload={onResumeUpload}
        submitting={submitting}
        upload={upload}
        wakeLockHeld={wakeLock.held}
        mobileHint={!isDesktop ? t('mobileUploadHint') : null}
        showCost={isAdminOrManager}
        error={submitError}
        theme={theme}
        accent={accent}
        t={t}
      />

      {isAdminOrManager && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 16,
            marginBottom: 12,
          }}
        >
          <label
            style={{
              fontSize: 12,
              color: theme.textMuted,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
            />
            {t('trShowAllUsers')}
          </label>
          <button
            onClick={triggerReload}
            disabled={loading}
            style={{
              marginInlineStart: 'auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              borderRadius: 8,
              border: `1px solid ${theme.border}`,
              background: theme.surface,
              color: theme.text,
              fontSize: 12,
              fontWeight: 600,
              cursor: loading ? 'wait' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <Icon name="refresh" size={12} />
            {t('trRefresh')}
          </button>
        </div>
      )}

      {loading && jobs == null && <ListSkeleton theme={theme} />}
      {!loading && err && (
        <ErrorPanel
          theme={theme}
          accent={accent}
          message={err}
          onRetry={triggerReload}
          retryLabel={t('trTryAgain')}
        />
      )}
      {!err && sortedJobs && sortedJobs.length === 0 && !loading && (
        <EmptyState
          theme={theme}
          label={
            showAll ? t('trEmptyAll') : t('trEmptyMine')
          }
          variant="bare"
        />
      )}
      {!err && sortedJobs && sortedJobs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sortedJobs.map((job) => (
            <JobRow
              key={job._id}
              job={job}
              t={t}
              detail={details[job._id] ?? null}
              detailError={detailErrors[job._id] ?? null}
              onRetryDetail={retryDetail}
              expanded={expandedId === job._id}
              onToggle={() => setExpandedId((cur) => (cur === job._id ? null : job._id))}
              onRetry={() => onRetry(job)}
              onDelete={() => onDelete(job)}
              onToast={pushToast}
              isAdminOrManager={isAdminOrManager}
              showOwner={showAll}
              theme={theme}
              accent={accent}
            />
          ))}
        </div>
      )}

      <ToastTray toasts={toasts} theme={theme} rtl={rtl} />
    </div>
  );
}

// ─── Submit form ─────────────────────────────────────────────────────

/** Upload UI state shared with SubmitForm / FilePickerArea. */
interface UploadUiBase {
  /** 0..1 */
  progress: number;
  bytesTransferred: number;
  /** null until measured (first bytes + 5 s + ≥ 1 %). */
  etaSeconds: number | null;
}
interface UploadAwaitingFile extends UploadUiBase {
  phase: 'awaiting-file';
  file: null;
  /** The stored session waiting for its file to be re-picked. */
  record: ResumableSessionRecord;
}
interface UploadWithFile extends UploadUiBase {
  phase: 'confirm' | 'uploading' | 'paused' | 'interrupted' | 'finalizing';
  file: File;
  record: ResumableSessionRecord | null;
  /** Readable reason when phase === 'interrupted' for good (rules, quota). */
  errorMessage?: string;
  /** Set when this run continued an earlier session. */
  resumedFromPct?: number | null;
  /** Probed media duration (seconds); undefined = probing, null = unknown. */
  durationSec?: number | null;
}
type UploadUiState = UploadAwaitingFile | UploadWithFile;

interface SubmitFormProps {
  url: string;
  /** Local verdict: false disables the button and Enter. */
  urlValid: boolean;
  /** Field-level message under the input while the text is not a URL. */
  urlError: string | null;
  /** Translated "Supported formats" label. */
  formatsLabel: string;
  onUrlChange: (v: string) => void;
  onSubmitUrl: () => void;
  onSubmitFile: (file: File) => void;
  onCancelUpload: () => void;
  onConfirmUpload: () => void;
  onResumeUpload: () => void;
  submitting: boolean;
  upload: UploadUiState | null;
  wakeLockHeld: boolean;
  /** Fixed hint under the drop area on phones; null on desktop. */
  mobileHint: string | null;
  /** Admin/manager: show the estimated cost next to the duration. */
  showCost: boolean;
  error: string | null;
  theme: ThemeTokens;
  accent: AccentTokens;
  t: (key: string) => string;
}

function SubmitForm({
  url,
  urlValid,
  urlError,
  formatsLabel,
  onUrlChange,
  onSubmitUrl,
  onSubmitFile,
  onCancelUpload,
  onConfirmUpload,
  onResumeUpload,
  submitting,
  upload,
  wakeLockHeld,
  mobileHint,
  showCost,
  error,
  theme,
  accent,
  t,
}: SubmitFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const onPickClick = () => {
    if (!submitting) fileInputRef.current?.click();
  };
  const onFileChosen = (file: File | null | undefined) => {
    if (!file) return;
    onSubmitFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div
      style={{
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: 12,
        padding: 14,
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: theme.text,
          marginBottom: 6,
        }}
      >
        {t('trSourceUrl')}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          type="url"
          value={url}
          onChange={(e) => onUrlChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !submitting && urlValid) onSubmitUrl();
          }}
          aria-invalid={urlError != null}
          placeholder={t('trUrlPlaceholder')}
          disabled={submitting}
          style={{
            flex: 1,
            minWidth: 240,
            padding: '8px 10px',
            borderRadius: 8,
            border: `1px solid ${urlError ? '#fca5a5' : theme.border}`,
            background: theme.bg,
            color: theme.text,
            fontSize: 13,
            fontFamily: 'inherit',
          }}
        />
        <button
          onClick={onSubmitUrl}
          disabled={submitting || !urlValid}
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            border: 'none',
            background: accent.main,
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: submitting || !urlValid ? 'not-allowed' : 'pointer',
            opacity: submitting || !urlValid ? 0.6 : 1,
            fontFamily: 'inherit',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Icon name="send" size={12} />
          {submitting && upload == null ? t('trSubmitting') : t('trTranslate')}
        </button>
      </div>
      {urlError && (
        <div role="alert" style={{ marginTop: 6, fontSize: 11, color: '#b91c1c', lineHeight: 1.4 }}>
          {urlError}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 10,
          fontSize: 11,
          color: theme.textMuted,
        }}
      >
        <span style={{ flex: 1, height: 1, background: theme.border }} />
        <span style={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>{t('trOr')}</span>
        <span style={{ flex: 1, height: 1, background: theme.border }} />
      </div>

      <FilePickerArea
        dragOver={dragOver}
        setDragOver={setDragOver}
        onPickClick={onPickClick}
        onFileChosen={onFileChosen}
        disabled={submitting || (upload != null && upload.phase !== 'awaiting-file')}
        upload={upload}
        wakeLockHeld={wakeLockHeld}
        onCancel={onCancelUpload}
        onConfirm={onConfirmUpload}
        onResume={onResumeUpload}
        formatsHint={`${formatsLabel}: ${SUPPORTED_FORMATS_LABEL}`}
        mobileHint={mobileHint}
        showCost={showCost}
        theme={theme}
        accent={accent}
        t={t}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept={MEDIA_FILE_ACCEPT}
        onChange={(e) => onFileChosen(e.target.files?.[0])}
        style={{ display: 'none' }}
      />

      {error && (
        <div
          role="alert"
          style={{
            marginTop: 8,
            padding: '8px 10px',
            borderRadius: 8,
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            fontSize: 12,
            fontWeight: 500,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: FONT_STACK.mono,
            lineHeight: 1.5,
          }}
        >
          {error}
        </div>
      )}
      <div
        style={{
          marginTop: 8,
          fontSize: 11,
          color: theme.textMuted,
          opacity: 0.85,
        }}
      >
        {t('trLimitsNote')}
      </div>
    </div>
  );
}

interface FilePickerAreaProps {
  dragOver: boolean;
  setDragOver: (v: boolean) => void;
  onPickClick: () => void;
  onFileChosen: (file: File | null | undefined) => void;
  disabled: boolean;
  upload: UploadUiState | null;
  wakeLockHeld: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onResume: () => void;
  /** "Supported formats: MP4, …" shown under the drop area. */
  formatsHint: string;
  /** Phone-only hint (link is better, keep the screen on); null on desktop. */
  mobileHint: string | null;
  showCost: boolean;
  theme: ThemeTokens;
  accent: AccentTokens;
  t: (key: string) => string;
}

/** "Duration ≈ 2:14 · processing ≈ 0:35 · est. cost $1.60" — from the
 * probed media duration; null while probing / when unknown. */
function estimateLine(
  durationSec: number | null | undefined,
  showCost: boolean,
  t: (key: string) => string,
): string | null {
  if (durationSec == null || !Number.isFinite(durationSec) || durationSec <= 0) return null;
  const parts = [t('mediaDuration').replace('{d}', formatEta(durationSec))];
  const proc = estimateProcessingSeconds(durationSec);
  if (proc != null) parts.push(t('estimatedProcessing').replace('{d}', formatEta(proc)));
  if (showCost) {
    const cost = estimateCostUsd(durationSec);
    if (cost != null) parts.push(`${t('estimatedCost')} ${fmtUsd(cost) ?? ''}`.trim());
  }
  return parts.join(' · ');
}

function FilePickerArea({
  dragOver,
  setDragOver,
  onPickClick,
  onFileChosen,
  disabled,
  upload,
  wakeLockHeld,
  onCancel,
  onConfirm,
  onResume,
  formatsHint,
  mobileHint,
  showCost,
  theme,
  accent,
  t,
}: FilePickerAreaProps) {
  // The drop area stays clickable while we wait for the user to re-pick
  // the file of an earlier session; otherwise any upload state blocks it.
  const busy = upload != null && upload.phase !== 'awaiting-file';
  const pct = upload ? Math.round(upload.progress * 100) : 0;
  const smallBtn = (primary: boolean): CSSProperties => ({
    padding: '5px 12px',
    borderRadius: 6,
    border: `1px solid ${primary ? accent.main : theme.border}`,
    background: primary ? accent.main : theme.surface,
    color: primary ? '#fff' : theme.text,
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  });

  let inner: React.ReactNode;
  if (!upload) {
    inner = (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
          color: theme.textMuted,
          fontSize: 12,
        }}
      >
        <Icon name="film" size={18} />
        <div style={{ color: theme.text, fontWeight: 600 }}>
          {t('trUploadFile')}
        </div>
        <div style={{ fontSize: 11 }}>
          {t('trDropFile')}
        </div>
        <div style={{ fontSize: 10, color: theme.textSubtle, marginTop: 2, direction: 'ltr' }}>
          {formatsHint}
        </div>
        <div style={{ fontSize: 10, color: theme.textSubtle }}>{t('extensionNote')}</div>
      </div>
    );
  } else if (upload.phase === 'awaiting-file') {
    // A session survived a refresh; the File did not. Same file → we
    // continue from the server's offset; a different file → fresh start.
    inner = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: theme.text }}>
          {t('uploadInterrupted')} · {pct}%
        </div>
        <div
          style={{
            fontSize: 12,
            color: theme.text,
            direction: 'ltr',
            fontFamily: FONT_STACK.mono,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
        >
          {upload.record.fingerprint.name} · {formatBytes(upload.record.fingerprint.size)}
        </div>
        <div style={{ fontSize: 11, color: theme.textMuted, lineHeight: 1.45, maxWidth: 420 }}>
          {t('resumeFromPercent').replace('{p}', String(pct))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPickClick();
            }}
            style={smallBtn(true)}
          >
            {t('trChooseFile')}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCancel();
            }}
            style={smallBtn(false)}
          >
            {t('trCancel')}
          </button>
        </div>
      </div>
    );
  } else if (upload.phase === 'confirm') {
    // > 300 MB: say what is about to happen and offer the link route.
    const est = estimateLine(upload.durationSec, showCost, t);
    inner = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: theme.text }}>
          {t('largeFileTitle')} · {formatBytes(upload.file.size)}
        </div>
        <div
          style={{
            fontSize: 12,
            color: theme.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
            direction: 'ltr',
            fontFamily: FONT_STACK.mono,
          }}
        >
          {upload.file.name}
        </div>
        <div style={{ fontSize: 11, color: theme.textMuted, direction: 'ltr' }}>
          {est ??
            (upload.durationSec === undefined ? t('calculatingDuration') : t('durationUnknown'))}
        </div>
        <div style={{ fontSize: 11, color: theme.textMuted, lineHeight: 1.45, maxWidth: 420 }}>
          {t('largeFileBody')} {t('keepScreenOn')}.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onConfirm();
            }}
            style={smallBtn(true)}
          >
            {t('startUpload')}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCancel();
            }}
            style={smallBtn(false)}
          >
            {t('trCancel')}
          </button>
        </div>
      </div>
    );
  } else {
    const total = upload.file.size;
    const eta =
      upload.etaSeconds != null
        ? `${formatEta(upload.etaSeconds)} ${t('timeLeft')}`
        : t('calculating');
    const est = estimateLine(upload.durationSec, showCost, t);
    inner = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div
          style={{
            fontSize: 12,
            color: theme.text,
            fontWeight: 600,
            fontFamily: FONT_STACK.mono,
            display: 'flex',
            justifyContent: 'center',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <span>
            {upload.phase === 'uploading'
              ? t('uploading')
              : upload.phase === 'finalizing'
                ? t('finalizingUpload')
                : upload.phase === 'paused'
                  ? '⏸'
                  : '⚠'}{' '}
            {pct}%
          </span>
          <span style={{ color: theme.textMuted, direction: 'ltr' }}>
            {formatBytes(upload.bytesTransferred)} / {formatBytes(total)}
          </span>
          {upload.phase === 'uploading' && (
            <span style={{ color: theme.textMuted, direction: 'ltr' }}>{eta}</span>
          )}
        </div>
        <div
          style={{
            height: 6,
            borderRadius: 999,
            background: theme.border,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${pct}%`,
              background: upload.phase === 'interrupted' ? '#dc2626' : accent.main,
              transition: 'width 120ms linear',
            }}
          />
        </div>
        {(est || upload.durationSec === undefined) && (
          <div style={{ fontSize: 10, color: theme.textSubtle, direction: 'ltr' }}>
            {est ?? t('calculatingDuration')}
          </div>
        )}
        {upload.resumedFromPct != null &&
          upload.resumedFromPct > 0 &&
          upload.phase === 'uploading' && (
            <div style={{ fontSize: 10, color: theme.textSubtle }}>
              {t('resumingFrom').replace('{p}', String(upload.resumedFromPct))}
            </div>
          )}
        {upload.phase === 'paused' && (
          <div style={{ fontSize: 11, color: '#b45309', fontWeight: 600 }}>
            {t('uploadPausedOffline')}
          </div>
        )}
        {upload.phase === 'interrupted' && (
          <div
            role="alert"
            style={{ fontSize: 11, color: '#991b1b', fontWeight: 600, whiteSpace: 'pre-wrap' }}
          >
            {t('uploadInterrupted')}
            {upload.errorMessage ? ` — ${upload.errorMessage}` : ''}
          </div>
        )}
        {upload.phase === 'uploading' && (
          <div style={{ fontSize: 10, color: theme.textSubtle }}>
            {wakeLockHeld ? t('screenKeptOn') : t('keepScreenOn')}
          </div>
        )}
        {/* Finalizing: nothing left to cancel — the bytes are on the
         * server and the job POST is in flight. */}
        {upload.phase !== 'finalizing' && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            {(upload.phase === 'paused' || upload.phase === 'interrupted') && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onResume();
                }}
                style={smallBtn(true)}
              >
                {t('resumeUpload')}
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCancel();
              }}
              style={smallBtn(false)}
            >
              {t('trCancel')}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div
        onDragOver={(e) => {
          if (disabled && busy) return;
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (disabled && busy) return;
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files?.[0];
          onFileChosen(file);
        }}
        onClick={() => {
          if (!busy) onPickClick();
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !busy) {
            e.preventDefault();
            onPickClick();
          }
        }}
        style={{
          marginTop: 8,
          padding: '14px 12px',
          borderRadius: 10,
          border: `1.5px dashed ${dragOver ? accent.main : theme.border}`,
          background: dragOver ? `${accent.main}0d` : 'transparent',
          cursor: busy ? 'default' : 'pointer',
          textAlign: 'center',
          transition: 'border-color 120ms, background 120ms',
        }}
      >
        {inner}
      </div>
      {mobileHint && (
        <div style={{ marginTop: 6, fontSize: 11, color: theme.textMuted, lineHeight: 1.45 }}>
          {mobileHint}
        </div>
      )}
    </>
  );
}

// ─── Job row ─────────────────────────────────────────────────────────

interface JobRowProps {
  /** Slim list row (status, title, progress, timestamps, owner). */
  job: TranslationJob;
  t: (key: string) => string;
  /** Full document from GET /:id once fetched; null until then. */
  detail: TranslationJob | null;
  detailError: string | null;
  onRetryDetail: () => void;
  expanded: boolean;
  onToggle: () => void;
  onRetry: () => void;
  onDelete: () => void;
  onToast: (kind: Toast['kind'], message: string) => void;
  isAdminOrManager: boolean;
  showOwner: boolean;
  theme: ThemeTokens;
  accent: AccentTokens;
}

function JobRow({
  job,
  t,
  detail,
  detailError,
  onRetryDetail,
  expanded,
  onToggle,
  onRetry,
  onDelete,
  onToast,
  isAdminOrManager,
  showOwner,
  theme,
  accent,
}: JobRowProps) {
  // Merge: the document contributes segments / summaries / sourceUrl /
  // cost; the list row wins on everything it carries because it is
  // refetched at least as often (status, progress, retryCount, error).
  const full: TranslationJob = detail ? { ...detail, ...job } : job;
  const created = fmtTimestamp(job.createdAt);
  const dur = fmtDuration(job.durationSeconds);
  const label = jobLabel(full, t);
  const owner = ownerLabel(full);
  const isProcessing = job.status === 'claimed' || job.status === 'processing';
  // `full`: the list row may not carry retryable / errorCategory on every
  // api version; the detail does once expanded.
  const canRetry = canRetryJob(full, TRANSLATION_RETRY_LIMIT);
  const canDelete = job.status === 'failed' || job.status === 'complete';

  return (
    <div
      style={{
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: 12,
        padding: 12,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <button
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse' : 'Expand'}
          style={{
            border: 'none',
            background: 'transparent',
            padding: 4,
            cursor: 'pointer',
            color: theme.textMuted,
            display: 'inline-flex',
          }}
        >
          <Icon name={expanded ? 'chevronDown' : 'chevronRight'} size={14} />
        </button>
        <StatusBadge
          status={job.status}
          processing={isProcessing}
          theme={theme}
          accent={accent}
          t={t}
        />
        {/* `full` (list row merged with the detail): api ≤ 0.13 lists do
         * not carry queuePosition, the detail does; R-12 lists will. */}
        {job.status === 'pending' && full.queuePosition != null && full.queuePosition > 0 && (
          <span
            title={t('trQueuePosition')}
            style={{ fontSize: 11, color: theme.textMuted, fontFamily: FONT_STACK.mono }}
          >
            {full.queuePosition === 1
              ? t('queuedNext')
              : t('queuedPosition').replace('{n}', String(full.queuePosition - 1))}
          </span>
        )}
        {missingChunks(full).length > 0 && (
          <span
            title={t('trChunksNotTranscribed')}
            style={{
              padding: '2px 8px',
              borderRadius: 999,
              background: '#fef3c7',
              color: '#92400e',
              fontSize: 10,
              fontWeight: 700,
              whiteSpace: 'nowrap',
            }}
          >
            {missingChunks(full).length === 1
              ? t('missingChunkOne')
              : t('missingChunksN').replace('{n}', String(missingChunks(full).length))}
          </span>
        )}
        {full.sourceUrl ? (
          <a
            href={full.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={full.sourceUrl}
            onClick={(e) => e.stopPropagation()}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: theme.text,
              textDecoration: 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 360,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {label}
            <Icon name="externalLink" size={11} />
          </a>
        ) : (
          <span
            title={label}
            onClick={onToggle}
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: theme.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 360,
              cursor: 'pointer',
            }}
          >
            {label}
          </span>
        )}
        <div
          style={{
            marginInlineStart: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            fontSize: 11,
            color: theme.textMuted,
            fontFamily: FONT_STACK.mono,
          }}
        >
          {showOwner && owner && <span title={full.ownerUid ?? undefined}>{owner}</span>}
          {created && <span>{created}</span>}
          {dur && <span title={t('trSourceDuration')}>{dur}</span>}
          {job.retryCount > 0 && (
            <span title={t('trRetries')} style={{ color: '#d97706' }}>
              ↻ {job.retryCount}/{TRANSLATION_RETRY_LIMIT}
            </span>
          )}
        </div>
      </div>

      {isProcessing && (job.progressMessage || job.numChunks) && (
        <div
          style={{
            marginTop: 8,
            paddingInlineStart: 28,
            fontSize: 11,
            color: theme.textMuted,
            fontFamily: FONT_STACK.mono,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          {job.numChunks != null && job.numChunks > 0 && (
            <span>
              {t('chunkOf')
                .replace('{a}', String(job.chunksCompleted ?? 0))
                .replace('{b}', String(job.numChunks))}
            </span>
          )}
          {full.partial && full.segments && full.segments.length > 0 && (
            <span style={{ color: accent.main }}>{t('partialResults')}</span>
          )}
          {job.progressMessage && <span>{job.progressMessage}</span>}
        </div>
      )}

      {expanded && (
        <JobDetail
          job={full}
          t={t}
          detailLoaded={detail != null}
          detailError={detailError}
          onRetryDetail={onRetryDetail}
          onRetry={canRetry ? onRetry : null}
          onDelete={canDelete ? onDelete : null}
          onToast={onToast}
          isAdminOrManager={isAdminOrManager}
          theme={theme}
          accent={accent}
        />
      )}
    </div>
  );
}

// ─── Status badge ────────────────────────────────────────────────────

interface StatusBadgeProps {
  status: TranslationStatus;
  processing: boolean;
  theme: ThemeTokens;
  accent: AccentTokens;
  t: (key: string) => string;
}

function StatusBadge({ status, processing, theme, accent, t }: StatusBadgeProps) {
  const color = statusColor(status, accent);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '3px 8px',
        borderRadius: 999,
        background: `${color}1f`,
        color,
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        fontFamily: FONT_STACK.mono,
      }}
    >
      {processing ? (
        <SpinnerDot color={color} />
      ) : (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: color,
          }}
        />
      )}
      {statusLabel(status, t)}
      <style>{`
        @keyframes nayto-translator-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>
      {void theme}
    </span>
  );
}

function SpinnerDot({ color }: { color: string }) {
  const s: CSSProperties = {
    width: 8,
    height: 8,
    borderRadius: '50%',
    border: `1.5px solid ${color}`,
    borderTopColor: 'transparent',
    animation: 'nayto-translator-spin 0.9s linear infinite',
    display: 'inline-block',
  };
  return <span style={s} />;
}

// ─── Job detail panel ───────────────────────────────────────────────

interface JobDetailProps {
  /** List row merged with the full document (when fetched). */
  job: TranslationJob;
  t: (key: string) => string;
  /** False until GET /:id has returned — result / source link pending. */
  detailLoaded: boolean;
  detailError: string | null;
  onRetryDetail: () => void;
  onRetry: (() => void) | null;
  onDelete: (() => void) | null;
  onToast: (kind: Toast['kind'], message: string) => void;
  isAdminOrManager: boolean;
  theme: ThemeTokens;
  accent: AccentTokens;
}

function JobDetail({
  job,
  t,
  detailLoaded,
  detailError,
  onRetryDetail,
  onRetry,
  onDelete,
  onToast,
  isAdminOrManager,
  theme,
  accent,
}: JobDetailProps) {
  const [activeLang, setActiveLang] = useState<Lang>('en');

  const timeline: Array<{ label: string; iso: string | null | undefined }> = [
    { label: t('trCreated'), iso: job.createdAt },
    { label: t('trClaimed'), iso: job.claimedAt ?? null },
    { label: t('trStarted'), iso: job.startedAt ?? null },
    { label: t('trCompleted'), iso: job.completedAt ?? null },
  ];

  const cost = isAdminOrManager ? fmtUsd(job.costEstimateUsd) : null;

  return (
    <div
      style={{
        marginTop: 12,
        paddingTop: 12,
        borderTop: `1px solid ${theme.border}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          fontSize: 11,
          color: theme.textMuted,
          fontFamily: FONT_STACK.mono,
        }}
      >
        {timeline.map((step) => {
          const ts = fmtTimestamp(step.iso);
          return (
            <div key={step.label} style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ opacity: 0.7 }}>{step.label}</span>
              <span style={{ color: ts ? theme.text : theme.textSubtle }}>{ts ?? '—'}</span>
            </div>
          );
        })}
        {cost && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ opacity: 0.7 }}>{t('trCost')}</span>
            <span style={{ color: theme.text }}>{cost}</span>
          </div>
        )}
      </div>

      {(job.sourceUrl || job.originalFilename) && (
        <div
          style={{
            fontSize: 11,
            color: theme.textMuted,
            fontFamily: FONT_STACK.mono,
            display: 'flex',
            gap: 6,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ opacity: 0.7 }}>{t('trSource')}</span>
          {job.originalFilename && (
            <span style={{ color: theme.text }}>{job.originalFilename}</span>
          )}
          {job.sourceUrl && (
            <a
              href={job.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: theme.text,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 480,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              {job.sourceUrl.length > 80 ? `${job.sourceUrl.slice(0, 77)}…` : job.sourceUrl}
              <Icon name="externalLink" size={10} />
            </a>
          )}
        </div>
      )}

      {detailError && (
        <ErrorPanel
          theme={theme}
          accent={accent}
          message={detailError}
          onRetry={onRetryDetail}
          retryLabel={t('trTryAgain')}
        />
      )}

      {(job.status === 'pending' || job.status === 'claimed' || job.status === 'processing') &&
        (job.estimatedProcessingSeconds != null ||
          (isAdminOrManager && job.estimatedCostUsd != null)) && (
          <div
            style={{
              fontSize: 11,
              color: theme.textMuted,
              fontFamily: FONT_STACK.mono,
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            {job.estimatedProcessingSeconds != null && (
              <span>
                {t('estimatedProcessing').replace('{d}', formatEta(job.estimatedProcessingSeconds))}
              </span>
            )}
            {isAdminOrManager && job.estimatedCostUsd != null && (
              <span>
                {t('estimatedCost')} {fmtUsd(job.estimatedCostUsd)}
              </span>
            )}
          </div>
        )}

      {job.status === 'complete' && !detailLoaded && !detailError && (
        <div
          style={{
            fontSize: 12,
            color: theme.textMuted,
            fontFamily: FONT_STACK.mono,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <SpinnerDot color={accent.main} />
          {t('trLoadingResult')}
        </div>
      )}

      {/* Complete → the result. Processing with `partial: true` → the
       * chunks finished so far, tagged; the detail poll keeps adding.
       * Failed with saved segments (R-16 chunks_failed) → the partial
       * result above the failure block, with a gap row per missing chunk. */}
      {detailLoaded &&
        (job.status === 'complete' ||
          (job.partial === true && (job.segments?.length ?? 0) > 0) ||
          (job.status === 'failed' && (job.segments?.length ?? 0) > 0)) && (
          <ResultPanel
            job={job}
            partial={job.status !== 'complete' && job.status !== 'failed' && job.partial === true}
            onRetryChunks={onRetry}
            t={t}
            activeLang={activeLang}
            onLangChange={setActiveLang}
            onToast={onToast}
            theme={theme}
            accent={accent}
          />
        )}

      {job.status === 'failed' && (
        <FailureBlock job={job} theme={theme} accent={accent} t={t} />
      )}

      {(onRetry || onDelete) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {onRetry && (
            <button
              onClick={onRetry}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                border: `1px solid ${accent.main}`,
                background: accent.main,
                color: '#fff',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Icon name="refresh" size={12} />
              {missingChunks(job).length > 0 ? t('completeMissingChunks') : t('trRetry')}
            </button>
          )}
          {onDelete && (
            <button
              onClick={onDelete}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                border: `1px solid ${theme.border}`,
                background: theme.surface,
                color: '#dc2626',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Icon name="trash" size={12} />
              {t('trDelete')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface FailureBlockProps {
  job: TranslationJob;
  theme: ThemeTokens;
  accent: AccentTokens;
  t: (key: string) => string;
}

/** Category → i18n key for the failure title. Legacy (api ≤ 0.10) and
 * R-09 categories both covered; anything else falls through to "no
 * title", where errorMessage is shown as-is. */
const FAILURE_TITLE_KEY: Record<TranslationErrorCategory, string> = {
  unsupported_source: 'errUnsupportedSource',
  unsupported_media: 'errUnsupportedMedia',
  download_timeout: 'errDownloadTimeout',
  source_unavailable: 'errSourceUnavailable',
  unknown: 'errUnknown',
  user_fixable: 'errUserFixable',
  transient: 'errTransient',
  infrastructure: 'errInfrastructure',
};

/** Titles whose default wording invites a retry get a plain variant when
 * the failure is final (retryable:false or a final category). */
const FINAL_TITLE_KEY: Partial<Record<TranslationErrorCategory, string>> = {
  transient: 'errTransientFinal',
};

function failureTitle(
  job: Pick<TranslationJob, 'errorCategory' | 'retryable'>,
  t: (key: string) => string,
): string | null {
  const category = job.errorCategory;
  if (!category) return null;
  const finalKey = isFinalFailure(job) ? FINAL_TITLE_KEY[category] : undefined;
  const key = finalKey ?? FAILURE_TITLE_KEY[category];
  return key ? t(key) : null;
}

/** Failure block: readable title from errorCategory, errorMessage as the
 * second line, raw errorDetail collapsed under "Technical details".
 * Without a category (older api) the errorMessage is shown as-is. */
function FailureBlock({ job, theme, t }: FailureBlockProps) {
  const title = failureTitle(job, t);
  const gaps = missingChunks(job);
  const rawMessage = job.errorMessage ?? null;
  const message =
    rawMessage && /^chunks_failed\b/.test(rawMessage) && gaps.length > 0
      ? `${t('errChunksFailed')} (${t('missingChunksN').replace('{n}', String(gaps.length))})`
      : rawMessage;
  const chunkLines = formatChunkDetails(gaps);
  const detail = [job.errorDetail ?? '', chunkLines].filter((x) => x !== '').join('\n\n') || null;
  return (
    <div
      style={{
        background: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: 8,
        padding: 10,
        fontSize: 12,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          marginBottom: message || detail ? 6 : 0,
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '2px 8px',
            borderRadius: 999,
            background: '#dc2626',
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.4,
          }}
        >
          <Icon name="alert" size={10} />
          {title ??
            (message ? t('trFailed') : t('trUnknownError'))}
        </span>
        {job.errorCategory && (
          <span
            title={t('trCategory')}
            style={{ fontSize: 10, color: '#991b1b', fontFamily: FONT_STACK.mono, opacity: 0.8 }}
          >
            {job.errorCategory}
          </span>
        )}
      </div>
      {message && (
        <div style={{ color: '#991b1b', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {message}
        </div>
      )}
      {detail && (
        <details style={{ marginTop: 8 }}>
          <summary
            style={{
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: 600,
              color: '#991b1b',
              userSelect: 'none',
            }}
          >
            {t('technicalDetails')}
          </summary>
          <pre
            dir="ltr"
            style={{
              margin: '6px 0 0',
              padding: 8,
              background: theme.surface,
              border: `1px solid ${theme.border}`,
              borderRadius: 6,
              fontSize: 11,
              lineHeight: 1.45,
              color: theme.text,
              fontFamily: FONT_STACK.mono,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 240,
              overflow: 'auto',
              textAlign: 'left',
            }}
          >
            {detail}
          </pre>
        </details>
      )}
    </div>
  );
}

// ─── Result panel (4-lang tabs) ─────────────────────────────────────

interface ResultPanelProps {
  job: TranslationJob;
  /** True while the job is still processing and `segments` is what has
   * arrived so far. */
  partial?: boolean;
  /** Targeted retry for the missing chunks (same POST /:id/retry); null
   * when the job cannot be retried. */
  onRetryChunks?: (() => void) | null;
  t: (key: string) => string;
  activeLang: Lang;
  onLangChange: (l: Lang) => void;
  onToast: (kind: Toast['kind'], message: string) => void;
  theme: ThemeTokens;
  accent: AccentTokens;
}

function ResultPanel({
  job,
  partial = false,
  onRetryChunks = null,
  t,
  activeLang,
  onLangChange,
  onToast,
  theme,
  accent,
}: ResultPanelProps) {
  const summary = job.summaries?.[activeLang] ?? null;
  const segments = job.segments ?? null;
  const gaps = missingChunks(job);
  // 'off' for a job from before 0.18.0, and for one whose reconciliation
  // question came back null — see utils/speakers.
  const speakers = speakerMode(job);
  const title =
    activeLang === 'en'
      ? (job.englishTitle ?? job.title ?? null)
      : (job.title ?? job.englishTitle ?? null);
  const langIsRtl = activeLang === 'he';

  const onCopy = useCallback(async () => {
    const text = formatResultForCopy(job, activeLang);
    if (!text) {
      onToast('info', t('trNothingToCopy'));
      return;
    }
    const ok = await copyTextToClipboard(text);
    onToast(
      ok ? 'success' : 'error',
      ok ? t('trCopied') : t('trCopyFailed'),
    );
  }, [job, activeLang, onToast, t]);

  const onShare = useCallback(async () => {
    const url = buildShareUrl(job._id);
    const ok = await copyTextToClipboard(url);
    onToast(
      ok ? 'success' : 'error',
      ok ? t('trShareLinkCopied') : t('trCopyFailed'),
    );
  }, [job._id, onToast, t]);

  // Print / Save-as-PDF. The print stylesheet (injected once at the
  // screen level) hides everything except `.nayto-print-target`, so
  // when window.print() opens the browser's dialog the user sees only
  // the active language's result. The browser's "Save as PDF"
  // destination produces a real PDF without any deps. We mark <html>
  // with a class while printing so a few CSS tweaks (uniform white
  // background, no max-height on the segment list) can apply.
  // "Show more" pages the on-screen list; the PDF must contain every row.
  // flushSync renders the full list before the print dialog snapshots.
  const [printAll, setPrintAll] = useState(false);
  const onPrint = useCallback(() => {
    const html = document.documentElement;
    html.classList.add('nayto-printing');
    const cleanup = () => {
      html.classList.remove('nayto-printing');
      setPrintAll(false);
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    flushSync(() => setPrintAll(true));
    window.print();
    // Safari fires afterprint reliably; fall-back timer in case the
    // event never fires (some embedded WebViews).
    window.setTimeout(cleanup, 60_000);
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <div
          role="tablist"
          style={{
            display: 'inline-flex',
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: 10,
            padding: 2,
            gap: 2,
            width: 'fit-content',
          }}
        >
          {RESULT_TABS.map((tab) => {
            const active = tab.k === activeLang;
            return (
              <button
                key={tab.k}
                role="tab"
                aria-selected={active}
                onClick={() => onLangChange(tab.k)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 8,
                  border: 'none',
                  background: active ? accent.main : 'transparent',
                  color: active ? '#fff' : theme.textMuted,
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  cursor: 'pointer',
                  fontFamily: FONT_STACK.mono,
                  minWidth: 44,
                }}
              >
                {tab.l}
              </button>
            );
          })}
        </div>
        <div
          style={{
            marginInlineStart: 'auto',
            display: 'inline-flex',
            gap: 6,
          }}
        >
          <ResultActionButton
            icon="copy"
            label={t('trCopy')}
            onClick={onCopy}
            theme={theme}
          />
          <ResultActionButton
            icon="share"
            label={t('trShare')}
            onClick={onShare}
            theme={theme}
          />
          <ResultActionButton
            icon="printer"
            label="PDF"
            onClick={onPrint}
            theme={theme}
          />
        </div>
      </div>

      <div
        className="nayto-print-target"
        dir={langIsRtl ? 'rtl' : 'ltr'}
        style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        {title && (
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: theme.text,
              letterSpacing: -0.2,
            }}
          >
            {title}
          </div>
        )}
        {partial && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              alignSelf: 'flex-start',
              padding: '2px 8px',
              borderRadius: 999,
              background: `${accent.main}1f`,
              color: accent.main,
              fontSize: 10,
              fontWeight: 700,
              fontFamily: FONT_STACK.mono,
            }}
          >
            <SpinnerDot color={accent.main} />
            {t('partialResults')}
          </div>
        )}
        {!partial && gaps.length > 0 && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              alignSelf: 'flex-start',
              padding: '2px 8px',
              borderRadius: 999,
              background: '#fef3c7',
              color: '#92400e',
              fontSize: 10,
              fontWeight: 700,
              fontFamily: FONT_STACK.mono,
            }}
          >
            <Icon name="alert" size={10} />
            {t('partialSaved')} ·{' '}
            {gaps.length === 1
              ? t('missingChunkOne')
              : t('missingChunksN').replace('{n}', String(gaps.length))}
          </div>
        )}

        {job.participants && job.participants.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {job.participants.map((p) => (
              <Chip
                key={p}
                label={p}
                active={false}
                onClick={() => {}}
                theme={theme}
                accent={accent}
              />
            ))}
          </div>
        )}

        {summary && (
          <div
            style={{
              padding: 10,
              background: theme.surface,
              border: `1px solid ${theme.border}`,
              borderRadius: 8,
              fontSize: 13,
              color: theme.text,
              lineHeight: 1.55,
              whiteSpace: 'pre-wrap',
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: theme.textMuted,
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                marginBottom: 4,
                fontFamily: FONT_STACK.mono,
              }}
            >
              {t('trSummary')}
            </div>
            {summary}
          </div>
        )}

        {/* F-09: the numbering may restart between chunks. Said once,
         * above the transcript, rather than on every label — a warning
         * repeated forty times stops being read. */}
        {segments && segments.length > 0 && speakers === 'unreconciled' && (
          <div
            role="note"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 6,
              marginBottom: 6,
              fontSize: 11,
              lineHeight: 1.45,
              color: theme.textMuted,
            }}
          >
            <Icon name="info" size={12} />
            <span>{t('spkUnreconciled')}</span>
          </div>
        )}

        {segments && segments.length > 0 ? (
          <SegmentList
            segments={segments}
            gaps={gaps}
            onRetryChunks={onRetryChunks}
            activeLang={activeLang}
            theme={theme}
            langIsRtl={langIsRtl}
            t={t}
            forceAll={printAll}
            showSpeakers={speakers !== 'off'}
            roster={job.speakers ?? null}
          />
        ) : (
          !summary && (
            <div style={{ fontSize: 12, color: theme.textMuted, fontStyle: 'italic' }}>
              {t('trNoContentForLang')}
            </div>
          )
        )}
        {/* The per-chunk errorDetail; a failed job also lists it inside
         * its failure block, a complete-with-gaps job only here. */}
        {job.status !== 'failed' && gaps.length > 0 && (
          <details>
            <summary
              style={{
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 600,
                color: theme.textMuted,
                userSelect: 'none',
              }}
            >
              {t('technicalDetails')}
            </summary>
            <pre
              dir="ltr"
              style={{
                margin: '6px 0 0',
                padding: 8,
                background: theme.surface,
                border: `1px solid ${theme.border}`,
                borderRadius: 6,
                fontSize: 11,
                lineHeight: 1.45,
                color: theme.text,
                fontFamily: FONT_STACK.mono,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 240,
                overflow: 'auto',
                textAlign: 'left',
              }}
            >
              {formatChunkDetails(gaps)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

interface ResultActionButtonProps {
  icon: 'copy' | 'share' | 'printer';
  label: string;
  onClick: () => void;
  theme: ThemeTokens;
}

function ResultActionButton({ icon, label, onClick, theme }: ResultActionButtonProps) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 10px',
        borderRadius: 8,
        border: `1px solid ${theme.border}`,
        background: theme.surface,
        color: theme.text,
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      <Icon name={icon} size={12} />
      {label}
    </button>
  );
}

interface SegmentListProps {
  segments: TranslationSegment[];
  /** Missing chunks (R-16); one highlighted gap row each, in time order. */
  gaps?: TranslationFailedChunk[];
  onRetryChunks?: (() => void) | null;
  activeLang: Lang;
  theme: ThemeTokens;
  langIsRtl: boolean;
  t: (key: string) => string;
  /** Render every row regardless of paging (printing). */
  forceAll?: boolean;
  /** F-09. False for a job with no diarization, and for one whose
   * reconciliation came back null — see utils/speakers. */
  showSpeakers?: boolean;
  roster?: readonly TranslationSpeaker[] | null;
}

/** Above this many rows the list renders in pages of SEGMENT_PAGE with a
 * "Show more" button — a 1500-row transcript otherwise costs a visible
 * jank on phones. Below it everything renders at once (unchanged). */
const SEGMENT_PAGE_THRESHOLD = 500;
const SEGMENT_PAGE = 200;

function SegmentList({
  segments,
  gaps = [],
  onRetryChunks = null,
  activeLang,
  theme,
  langIsRtl,
  t,
  forceAll = false,
  showSpeakers = false,
  roster = null,
}: SegmentListProps) {
  // Skip rows with no text in the active language so an EN-only result
  // doesn't render four screens of empty rows under HE/HY/RU.
  const visible = useMemo(
    () =>
      mergeSegmentsWithGaps(
        segments.filter((seg) => {
          const text = seg[activeLang];
          return typeof text === 'string' && text.length > 0;
        }),
        gaps,
      ),
    [segments, gaps, activeLang],
  );
  // Which rows open a new speaker run. Computed on `visible`, AFTER the
  // language filter and the gap merge: adjacency has to mean adjacency
  // on screen, or a row hidden for having no Armenian text would merge
  // two runs that the reader sees as separate.
  const runStarts = useMemo(
    () =>
      showSpeakers
        ? speakerRunStarts(
            visible.map((row) => (row.kind === 'segment' ? (row.seg.speaker?.id ?? null) : null)),
          )
        : [],
    [visible, showSpeakers],
  );

  const paged = !forceAll && visible.length > SEGMENT_PAGE_THRESHOLD;
  const [limit, setLimit] = useState(SEGMENT_PAGE);
  // New language tab / new job → start from the first page again.
  useEffect(() => {
    setLimit(SEGMENT_PAGE);
  }, [activeLang, segments]);
  const shown = paged ? visible.slice(0, limit) : visible;
  const remaining = paged ? Math.max(0, visible.length - shown.length) : 0;
  if (visible.every((r) => r.kind === 'gap')) {
    return (
      <div style={{ fontSize: 12, color: theme.textMuted, fontStyle: 'italic' }}>
        {t('trNoTranscriptForLang')}
      </div>
    );
  }
  return (
    <div
      data-print-scroll
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        maxHeight: 360,
        overflowY: 'auto',
      }}
    >
      {shown.map((row, i) => {
        const border = i < shown.length - 1 || remaining > 0 ? `1px solid ${theme.border}` : 'none';
        if (row.kind === 'gap') {
          // Missing chunk: highlighted row where its audio belongs, with
          // the targeted retry (same POST /:id/retry as the main button).
          return (
            <div
              key={`gap-${row.chunk.index}`}
              role="note"
              data-missing-chunk={row.chunk.index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '6px 10px',
                borderBottom: border,
                background: '#fef3c7',
                color: '#92400e',
                fontSize: 12,
                fontWeight: 600,
                lineHeight: 1.5,
              }}
            >
              <Icon name="alert" size={12} />
              <span style={{ flex: 1 }}>
                {t('missingChunkRange').replace('{range}', formatChunkRange(row.chunk))}
              </span>
              {onRetryChunks && (
                <button
                  onClick={onRetryChunks}
                  style={{
                    padding: '3px 10px',
                    borderRadius: 6,
                    border: '1px solid #92400e',
                    background: '#fff',
                    color: '#92400e',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {t('completeChunk')}
                </button>
              )}
            </div>
          );
        }
        const seg = row.seg;
        const label =
          showSpeakers && runStarts[i] ? speakerLabel(seg.speaker, roster, t) : null;
        return (
          <div
            key={`${seg.start}-${row.index}`}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: '6px 10px',
              borderBottom: border,
              fontSize: 12,
              color: theme.text,
              lineHeight: 1.5,
            }}
          >
            {/* Once per run, not once per line: a two-minute answer is
             * forty segments, and forty repetitions of "דובר א׳" is
             * noise the eye has to read past to find the words. */}
            {label && (
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: theme.textMuted,
                  letterSpacing: 0.2,
                }}
              >
                {label}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
            <span
              style={{
                flexShrink: 0,
                minWidth: 48,
                fontFamily: FONT_STACK.mono,
                color: theme.textMuted,
                fontVariantNumeric: 'tabular-nums',
                direction: 'ltr',
                textAlign: langIsRtl ? 'right' : 'left',
              }}
            >
              [{fmtMmSs(seg.start)}]
            </span>
            <span style={{ flex: 1, whiteSpace: 'pre-wrap' }}>{seg[activeLang]}</span>
            </div>
          </div>
        );
      })}
      {remaining > 0 && (
        <button
          onClick={() => setLimit((l) => l + SEGMENT_PAGE)}
          style={{
            padding: '8px 10px',
            border: 'none',
            background: theme.tint,
            color: theme.text,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {t('showMore')} · {t('rowsRemaining').replace('{n}', String(remaining))}
        </button>
      )}
    </div>
  );
}

// ─── List skeleton ──────────────────────────────────────────────────

function ListSkeleton({ theme }: { theme: ThemeTokens }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <SkeletonStyles />
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: 12,
            padding: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <Skeleton theme={theme} width={70} height={18} variant="pulse" />
          <Skeleton theme={theme} width="40%" height={14} variant="pulse" />
          <div style={{ marginInlineStart: 'auto' }}>
            <Skeleton theme={theme} width={120} height={11} variant="pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Toast tray ─────────────────────────────────────────────────────

interface ToastTrayProps {
  toasts: Toast[];
  theme: ThemeTokens;
  rtl: boolean;
}

function ToastTray({ toasts, theme, rtl }: ToastTrayProps) {
  if (toasts.length === 0) return null;
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 80,
        insetInlineEnd: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        zIndex: 200,
        pointerEvents: 'none',
      }}
    >
      {toasts.map((tt) => {
        const color =
          tt.kind === 'error' ? '#dc2626' : tt.kind === 'success' ? '#16a34a' : theme.text;
        return (
          <div
            key={tt.id}
            style={{
              padding: '8px 12px',
              borderRadius: 8,
              background: theme.surface,
              border: `1px solid ${color}`,
              color,
              fontSize: 12,
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              maxWidth: 320,
              direction: rtl ? 'rtl' : 'ltr',
            }}
          >
            {tt.message}
          </div>
        );
      })}
    </div>
  );
}
